import React, { useEffect, useState } from 'react';

import cx from 'classnames';

import { isActionTriggerLoading, SPINNER_CLASS } from '../widget.helper';

import { AnimationComponent } from '../../Animations';
import { useActionExectioner } from '../core/actions';
import { HotspotTrigger } from '../../../components/hotspot';
import { useOnScreen, getHotspotWidget } from '../core/hooks';
import { getDropShadow } from '../../../services/cssHelper.service';

import { ReactSVG } from 'react-svg';

import { getColorsConfig } from '../../../components/AssetViewer/helper/cssHelper';

import styles from './shape.module.scss';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';


const ShapeWidgetComponent = ({ className, style, widgetProps, breakpoint, onClick }) => {
  const actionExcutioner = useActionExectioner();

  const ref = React.useRef();

  const id = get(widgetProps, 'uid');
  const content = get(widgetProps, 'content');
  const action = get(widgetProps, 'actions.0');

  const properties = get(widgetProps, `properties.${breakpoint}`);
  const defaultProperties = get(widgetProps, `properties.desktop`, {});
  const animation = get(widgetProps, `properties.${breakpoint}.animation`) || {};
  const layout = get(properties, 'layout');
  const fillColor = get(defaultProperties, `fill`);
  const stroke = get(defaultProperties, `stroke`);
  const flip =  get(properties, `flip`);
  const shadow = get(defaultProperties, `shadow`, {});
  const hotspotWidget = getHotspotWidget(id);

  var widgetStyle = {
    ...style,
    ...getStyles(defaultProperties)
  }

  if (action || hotspotWidget) {
    widgetStyle = {
      ...widgetStyle,
      cursor: 'pointer'
    }
  }

  const handleClick = onClick ? onClick : (e) => {
    if (action) {
      e.stopPropagation();
      actionExcutioner.execute(widgetProps, action);
    }
  }

  return (
    <HotspotTrigger widget={widgetProps} breakpoint={breakpoint} targetRef={ref}>
      <div id={id}
        ref={ref}
        className={cx(className, styles.shape, {
          [SPINNER_CLASS]: isActionTriggerLoading(action)
        })}
        style={widgetStyle}
        data-widget-id={id}
        onClick={handleClick}
      >
        <AnimationComponent {...animation}>
          <Shape
            url={content}
            width={layout.width}
            height={layout.height}
            fill={ fillColor?.color || fillColor}
            flip={flip}
            stroke={stroke}
            shadow={shadow}
          />
        </AnimationComponent>
      </div>
    </HotspotTrigger>
  );
}

export const Shape = ({ url, width, height, fill, stroke, flip, shadow }) => {
  const elementRef = React.useRef(null);
  const isVisible = useOnScreen(elementRef)

  const fillColor = getColorsConfig(fill);
  const strokeColor = getColorsConfig(get(stroke, 'color'));
  const strokeWidth = parseInt(get(stroke, 'width', 0));
  const horizontalFlip = get(flip, 'horizontal');
  const verticalFlip = get(flip, 'vertical');
  const rotate = getFlipStyles(horizontalFlip, verticalFlip);

  const style = {
    fill: fillColor,
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    height: height,
    width: width,
    transform: rotate
  }

  useEffect(() => {
    calculateViewBox();
  }, [width, height, strokeWidth])

  const calculateViewBox = () => {
    if (strokeWidth <= 0 || !elementRef.current || !isVisible) {
      return;
    }

    const graphicElement = elementRef.current.querySelector("g");
    const box = graphicElement.getBBox();
    const viewBox = [-strokeWidth / 2, -strokeWidth / 2, box.width + strokeWidth, box.height + strokeWidth];
    elementRef.current.setAttribute('viewBox', viewBox.join(" "));
  }

  return (
    <ReactSVG
      className={styles.svgWrapper}
      width={width}
      height={height}
      src={url}
      style={style}
      afterInjection={(error, svg) => {
        elementRef.current = svg;
        calculateViewBox();
      }}
    />
  );
}

const shapeWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const ShapeWidget = React.memo(ShapeWidgetComponent, shapeWidgetEquals);


function getStyles(properties, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  //fill-color
  const fillRgb = get(properties, 'fill', null);
  const fill = getFillStyles(fillRgb);
  styles = {
    ...styles,
    ...fill
  }

  //stroke-color
  const strokeRgb = get(properties, 'stroke', null);
  const stroke = getStrokeStyles(strokeRgb);
  styles = {
    ...styles,
    ...stroke
  }

  //DropShadow
  const shadow = get(properties, 'shadow', {});
  const shadowStyle = getDropShadow(shadow);
  styles = {
    ...styles,
    ...shadowStyle
  }

  return styles;

}

//flip-shape
export function getFlipStyles(horizontal, vertical) {
  var transform = '';

  if(!vertical && !horizontal) {
    return 'rotate(0deg)';
  }

  if(vertical) {
    transform = 'rotateX(180deg)';
  }

  if(horizontal) {
    transform = `${transform} rotateY(180deg)`
  }

  return transform;
}

export function getFillStyles(value) {
  if (!value) {
    return null;
  }

  const fillRgb = value;

  var fill = 'inherit';
  if (fillRgb) {
    fill = `rgba(${fillRgb.r}, ${fillRgb.g}, ${fillRgb.b}, ${fillRgb.a})`;
  }

  var styles = {
    fill: fill
  };

  return styles;
}

export function getStrokeStyles(value) {
  if (!value) {
    return null;
  }

  const strokeRgb = get(value, 'color');
  const strokeWidth = get(value, 'width', 0);

  var stroke = 'inherit';
  if (strokeRgb) {
    stroke = `rgba(${strokeRgb.r}, ${strokeRgb.g}, ${strokeRgb.b}, ${strokeRgb.a})`;
  }

  var styles = {
    'stroke': stroke,
    'strokeWidth': strokeWidth
  };

  return styles;
}
